import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, Outlet, useLocation } from 'react-router';
import { EmojiObjectsRounded } from '@mui/icons-material';
import classNames from 'classnames';

// Components
import { CustomTooltip, Profile, Rail, Summary, TranscriptFacets } from 'components';

// Hooks
import { useAuth } from 'hooks';

// Store
import { userProfileFullNameSelector, userProfileSelector } from 'store/profile/selectors';
import { IUserProfile, useAppDispatch } from 'core';
import { closeRail } from 'store/rail/actions';
import { isRailOpenSelector } from 'store/rail/selectors';

// Styles.
import styles from './ProfilePage.module.scss';

// Types
import { INITIAL_TRANSCRIPTS_FACETS } from 'components/Facets/TranscriptFacets/constants';

const tooltipText =
  'No transcripts listed? Transcripts will only be displayed if we can match you to a participating certifying or licensing board in the United States, you have given permission to the accredited CME provider, and they have reported your completion.';
const tooltipLabel = 'View My Transcripts tooltip label.';

export const ProfilePage = (): ReactElement => {
  useAuth();
  const dispatch = useAppDispatch();
  const isTranscripts: boolean = useLocation()?.pathname?.includes('transcripts');
  const location = useLocation();

  // Selectors
  const userProfile: IUserProfile = useSelector(userProfileSelector);
  const fullName: string = useSelector(userProfileFullNameSelector);
  const isRailOpen: boolean = useSelector(isRailOpenSelector);

  const isClaimed: boolean = userProfile?.isClaimed;

  const onCloseRail = () => dispatch(closeRail());

  return (
    <>
      <Rail handleClose={onCloseRail} isOpen={isRailOpen}>
        <h5>{fullName}</h5>
        <nav className={styles.navigation}>
          <ul>
            <li>
              {/* manually setting selected here for styling */}
              <NavLink
                className={({ isActive }) =>
                  classNames(styles.link, { [styles.selected]: isActive && location.pathname === '/profile' })
                }
                onClick={onCloseRail}
                to="/profile"
              >
                Home
              </NavLink>
            </li>
            <li>
              {isClaimed ? (
                <NavLink
                  className={({ isActive }) => classNames(styles.link, { [styles.selected]: isActive })}
                  onClick={onCloseRail}
                  to="/profile/transcripts"
                >
                  View My Transcripts
                </NavLink>
              ) : (
                <>
                  <span className={classNames(styles.link, styles.disabled)}>View My Transcripts</span>{' '}
                  <CustomTooltip iconTooltip tooltipText={tooltipText}>
                    {tooltipLabel}
                  </CustomTooltip>
                </>
              )}
            </li>
            <li>
              <NavLink
                className={({ isActive }) => classNames(styles.link, { [styles.selected]: isActive })}
                onClick={onCloseRail}
                to="/profile/account-settings"
              >
                Account Settings
              </NavLink>
            </li>
          </ul>
        </nav>
        {isTranscripts ? (
          <TranscriptFacets handleClose={onCloseRail} initialValues={INITIAL_TRANSCRIPTS_FACETS} />
        ) : (
          <div className={styles.cta}>
            <EmojiObjectsRounded />
            <p>Do you have an idea for a feature you would like to see? Please send your idea to:</p>
            <a href="mailto:info@cmepassport.org">info@cmepassport.org</a>
          </div>
        )}
      </Rail>
      <Summary hasExploreMore>
        {location.pathname === '/profile' && <Profile />}
        <Outlet />
      </Summary>
    </>
  );
};
